/*
 * pfc-mount-pattern-core.js — SHARED mount-pattern render core
 * =============================================================
 * Pure, DOM-free, resolution-independent. Works as a browser asset AND as a
 * node `require()` (node-canvas) module. This is the CANONICAL copy consumed by
 * the print-file admin app (docs/PRINT-FILE-ADMIN-APP.md) to emit cutter-ready
 * mount print files at 300dpi.
 *
 * ⚠️ KEEP IN LOCKSTEP with assets/pfc-moulding.js — the live customiser carries
 *    its own inline copy of MOUNT_PATTERNS / STRIPE_PRESETS / STRIPE_BASE_HEX /
 *    stripeBandCount / paintMountFill for the on-PDP preview. Any change to a
 *    pattern's geometry, a new pattern, or the fill maths MUST be mirrored in
 *    BOTH files (and re-copied into the dropshipping-project). See the sync rule
 *    in CLAUDE.md. The `_addons_custom` property keys (Mount / Pattern /
 *    Pattern colour / Frame size) are produced by pfc-moulding.js ~L3808-3820.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api; // node / app
  if (typeof root !== 'undefined') root.PFCMountCore = api;                // browser global
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var STRIPE_BASE_HEX = '#fafaf6';   // white mount base
  // Mirror of MOUNT_PATTERNS in pfc-moulding.js. Geometry in real mm so density
  // is identical at any DPI / board size (continuous patterns stay seamless).
  var MOUNT_PATTERNS = [
    // `group`/`groupLabel`/`scaleLabel` drive a two-tier picker (style -> scale)
    // without changing keys/SKUs: patterns sharing a `group` collapse under one
    // style button and expand into scale chips. Order = fine -> bold within a group.
    { key: 'fine',     label: 'Fine 3mm stripes', type: 'stripes', bandWidthMm: 3, group: 'stripe',      groupLabel: 'Stripe',      scaleLabel: 'Fine' },
    { key: 'vertical', label: 'Vertical stripes', type: 'stripes', bands: 19,      group: 'stripe',      groupLabel: 'Stripe',      scaleLabel: 'Regular' },
    // Watercolour finish — hand-painted "Brushstroke Stripe" motif. Scales differ
    // by band/gap proportion only; geometry (wobble + pooling) shared in paintWatercolourStripes.
    { key: 'wc-fine',   label: 'Watercolour stripe — Fine',   type: 'watercolour', bandMm: 12, gapMm: 8,  wobbleMm: 1.8, poolAmt: 0.45, seed: 7, group: 'watercolour', groupLabel: 'Watercolour', scaleLabel: 'Fine' },
    { key: 'wc-even',   label: 'Watercolour stripe — Even',   type: 'watercolour', bandMm: 22, gapMm: 18, wobbleMm: 3.0, poolAmt: 0.55, seed: 7, group: 'watercolour', groupLabel: 'Watercolour', scaleLabel: 'Regular' },
    { key: 'wc-cabana', label: 'Watercolour stripe — Cabana', type: 'watercolour', bandMm: 34, gapMm: 9,  wobbleMm: 3.4, poolAmt: 0.60, seed: 7, group: 'watercolour', groupLabel: 'Watercolour', scaleLabel: 'Bold' },
    { key: 'gingham',  label: 'Gingham',          type: 'gingham', cellMm: 16,           group: 'gingham', groupLabel: 'Gingham' },
    { key: 'checker',  label: 'Checkered',        type: 'checkers', checkMm: 15,          group: 'checker', groupLabel: 'Checked' },
    { key: 'polka',    label: 'Polka dot',        type: 'polka', dotMm: 18,              group: 'polka',   groupLabel: 'Polka dot' },
    { key: 'chevron',  label: 'Chevron',          type: 'chevron', bandMm: 14,           group: 'chevron', groupLabel: 'Chevron' },
    { key: 'accent',   label: 'Accent keyline',   type: 'accent', gapMm: 10, lineMm: 1.4, group: 'accent', groupLabel: 'Keyline' },
    // French-line keylines (docs/SPEC-occasion-mount-templates.md, part A).
    // `hidden`: drawn and printed, but left out of the pattern pickers until the
    // on-board test print signs off the 0.3mm hairlines. Occasion templates
    // select them by key.
    { key: 'accent-fine',   label: 'Fine keyline',       type: 'accent', gapMm: 8, lineMm: 0.3,                                 group: 'accent', groupLabel: 'Keyline', hidden: true },
    { key: 'accent-double', label: 'Double keyline',     type: 'accent', gapMm: 8, lineMm: 0.3, lines: 2, spacingMm: 3,         group: 'accent', groupLabel: 'Keyline', hidden: true },
    { key: 'accent-wash',   label: 'French line & wash', type: 'accent', gapMm: 6, lineMm: 0.3, lines: 2, spacingMm: 6, washAlpha: 0.12, group: 'accent', groupLabel: 'Keyline', hidden: true }
  ];
  var STRIPE_PRESETS = [
    { label: 'Navy',       hex: '#23395d' },
    { label: 'Black',      hex: '#1a1a1a' },
    { label: 'Sage',       hex: '#7d8c6a' },
    { label: 'Burgundy',   hex: '#6e2433' },
    { label: 'Mustard',    hex: '#c79a3a' },
    { label: 'Dusty pink', hex: '#c98b8b' },
    { label: 'Slate grey', hex: '#6b7280' },
    { label: 'Teal',       hex: '#2e6e6a' }
  ];

  function normaliseHex(h) {
    if (!h) return null;
    h = String(h).trim();
    if (h[0] !== '#') h = '#' + h;
    return /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(h) ? h : null;
  }
  function patternByKey(key) {
    return MOUNT_PATTERNS.find(function (p) { return p.key === key; }) || MOUNT_PATTERNS[0];
  }
  function patternByLabel(label) {
    if (!label) return null;
    var l = String(label).trim().toLowerCase();
    return MOUNT_PATTERNS.find(function (p) { return p.label.toLowerCase() === l; }) || null;
  }
  function stripeBandCount(boardW_mm, pat) {
    if (pat.bands) return pat.bands;
    if (pat.bandWidthMm) { var n = Math.round((boardW_mm || 210) / pat.bandWidthMm); return n < 5 ? 5 : n; }
    return 19;
  }
  // Two-tier picker model: collapse MOUNT_PATTERNS by `group` into styles, each
  // carrying its ordered scale variants (patterns). Single-variant groups have
  // no scale sub-choice. Preserves array order.
  function patternGroups() {
    var order = [], map = {};
    MOUNT_PATTERNS.forEach(function (p) {
      if (p.hidden) return;   // printable, not offered in pickers
      var g = p.group || p.key;
      if (!map[g]) { map[g] = { group: g, label: p.groupLabel || p.label, items: [] }; order.push(map[g]); }
      map[g].items.push(p);
    });
    return order;
  }
  function groupOf(key) {
    var p = patternByKey(key), g = p.group || p.key;
    return patternGroups().find(function (x) { return x.group === g; });
  }

  /* ---- watercolour (hand-painted) stripe — deterministic, DOM-free ----
     Seeded 1D value-noise wobbles each band's two edges independently; a darker
     translucent edge stroke reads as pigment pooling; faint internal wash streaks
     give the painterly body. No offscreen canvas / grain (keeps it node-safe).
     Same seed → identical output, so the 300dpi print matches any preview. */
  function _wcRand(a) { return function () { a |= 0; a = a + 0x6D2B79F5 | 0; var t = Math.imul(a ^ a >>> 15, 1 | a); t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t; return ((t ^ t >>> 14) >>> 0) / 4294967296; }; }
  function _wcNoise(seed) { var rnd = _wcRand(seed >>> 0), g = new Float32Array(512); for (var i = 0; i < 512; i++) g[i] = rnd() * 2 - 1; return function (x) { var i = Math.floor(x), f = x - i, a = g[(i & 511)], b = g[((i + 1) & 511)], u = f * f * (3 - 2 * f); return a + (b - a) * u; }; }
  function _wcDisp(noise, t, phase) { return noise(t * 1.0 + phase) * 0.7 + noise(t * 2.7 + phase * 1.7) * 0.3; }
  function _wcRgb(hex) { hex = String(hex || '#888888').replace('#', ''); if (hex.length === 3) hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2]; var n = parseInt(hex, 16); return [n >> 16 & 255, n >> 8 & 255, n & 255]; }
  function _wcShade(hex, amt) { var c = _wcRgb(hex), f = amt < 0 ? 0 : 255, p = Math.abs(amt); return 'rgb(' + (Math.round((f - c[0]) * p) + c[0]) + ',' + (Math.round((f - c[1]) * p) + c[1]) + ',' + (Math.round((f - c[2]) * p) + c[2]) + ')'; }
  function paintWatercolourStripes(c, x, y, w, h, boardWmm, pat, stripeColour) {
    var pxPerMm = w / (boardWmm || 210);
    var bandPx = (pat.bandMm || 26) * pxPerMm;
    var gapPx = (pat.gapMm || 14) * pxPerMm;
    var period = bandPx + gapPx; if (period <= 0) { bandPx = period = 1; }
    var nBands = Math.ceil(w / period) + 1;
    var ampPx = (pat.wobbleMm || 3) * pxPerMm;
    var vFreqMm = 52, pool = (pat.poolAmt != null ? pat.poolAmt : 0.55);
    var noise = _wcNoise((((pat.seed || 7) * 2654435761) >>> 0));
    var step = Math.max(4, h / 160);
    // Overscan the band path past the top/bottom edges so it fully covers them
    // under fractional coords / devicePixelRatio scaling (caller clips to the
    // board rect, so the bleed is trimmed cleanly — no white hairline at edges).
    var OV = Math.max(step, ampPx) + 2, yTop = y - OV, yBot = y + h + OV;
    function edgeX(baseX, phase, yy) { var t = ((yy - y) / pxPerMm) / vFreqMm; return baseX + _wcDisp(noise, t, phase) * ampPx; }
    for (var b = 0; b < nBands; b++) {
      var lBase = x + b * period, rBase = x + b * period + bandPx, lPh = (2 * b) * 11.37, rPh = (2 * b + 1) * 11.37, yy;
      function bandPath() {
        c.beginPath();
        for (yy = yTop; yy <= yBot; yy += step) { var px = edgeX(lBase, lPh, yy); if (yy === yTop) c.moveTo(px, yy); else c.lineTo(px, yy); }
        c.lineTo(edgeX(lBase, lPh, yBot), yBot);
        for (yy = yBot; yy >= yTop; yy -= step) c.lineTo(edgeX(rBase, rPh, yy), yy);
        c.closePath();
      }
      bandPath();
      c.fillStyle = _wcShade(stripeColour, (noise(b * 3.1) * 0.5) * 0.06); c.fill();
      if (pool > 0) {
        c.save(); c.clip();
        c.lineWidth = Math.max(2, ampPx * 0.9 + 3); c.strokeStyle = _wcShade(stripeColour, -0.35); c.globalAlpha = 0.16 * pool; c.stroke();
        c.lineWidth = Math.max(1, ampPx * 0.4 + 1.5); c.globalAlpha = 0.22 * pool; c.stroke();
        c.globalAlpha = 1; c.restore();
      }
      c.save(); bandPath(); c.clip();
      var streaks = 2 + ((b * 7) % 3);
      for (var s = 0; s < streaks; s++) {
        var sx = lBase + (0.2 + 0.6 * noise(b * 5 + s * 13)) * bandPx;
        var grd = c.createLinearGradient(sx - 6, 0, sx + 6, 0);
        grd.addColorStop(0, 'rgba(0,0,0,0)'); grd.addColorStop(0.5, _wcShade(stripeColour, -0.25)); grd.addColorStop(1, 'rgba(0,0,0,0)');
        c.globalAlpha = 0.05; c.fillStyle = grd; c.fillRect(sx - 6, yTop, 12, yBot - yTop);
      }
      c.globalAlpha = 1; c.restore();
    }
  }

  /* ---- the pure fill (byte-identical to paintMountFill in pfc-moulding.js) ---- */
  // spec = { premium:bool, pattern:{type,...}|null, stripeColour:hex, plainHex:hex,
  //          printMode:bool }
  // printMode (print file): the mount base is the REAL white board, so the
  // off-white base is left TRANSPARENT — only the coloured ink (pattern, text)
  // is drawn, and the output must be a PNG (alpha). Preview leaves it false →
  // off-white base, unchanged.
  function paintMountFill(c, x, y, w, h, boardWmm, spec) {
    var printMode = !!(spec && spec.printMode);
    if (spec && spec.premium && spec.pattern) {
      var pat = spec.pattern;
      var stripeColour = spec.stripeColour || '#888888';
      if (!printMode) { c.fillStyle = STRIPE_BASE_HEX; c.fillRect(x, y, w, h); }
      if (pat.type === 'stripes') {
        var n = stripeBandCount(boardWmm, pat);
        var bw = w / n;
        for (var i = 0; i < n; i++) {
          if (i % 2 === 0) { if (printMode) continue; c.fillStyle = STRIPE_BASE_HEX; }
          else { c.fillStyle = stripeColour; }
          c.fillRect(x + i * bw, y, Math.ceil(bw) + 1, h);
        }
      } else if (pat.type === 'checkers') {
        var pxPerMm = w / (boardWmm || 210);
        var cell = Math.max(6, (pat.checkMm || 15) * pxPerMm);
        var cols = Math.ceil(w / cell), rows = Math.ceil(h / cell);
        c.save(); c.beginPath(); c.rect(x, y, w, h); c.clip();
        c.fillStyle = stripeColour;
        for (var ry = 0; ry < rows; ry++) {
          for (var cx = 0; cx < cols; cx++) {
            if ((cx + ry) % 2 === 0) continue;
            c.fillRect(x + cx * cell, y + ry * cell, Math.ceil(cell) + 1, Math.ceil(cell) + 1);
          }
        }
        c.restore();
      } else if (pat.type === 'gingham') {
        var gpx = w / (boardWmm || 210);
        var gcell = Math.max(6, (pat.cellMm || 16) * gpx);
        c.save(); c.beginPath(); c.rect(x, y, w, h); c.clip();
        c.fillStyle = stripeColour; c.globalAlpha = 0.42;
        var gcols = Math.ceil(w / gcell), grows = Math.ceil(h / gcell);
        for (var gx = 0; gx < gcols; gx += 2) c.fillRect(x + gx * gcell, y, Math.ceil(gcell) + 1, h);
        for (var gy = 0; gy < grows; gy += 2) c.fillRect(x, y + gy * gcell, w, Math.ceil(gcell) + 1);
        c.globalAlpha = 1; c.restore();
      } else if (pat.type === 'polka') {
        var ppx = w / (boardWmm || 210);
        var pcell = Math.max(8, (pat.dotMm || 18) * ppx);
        var prd = pcell * 0.3;
        c.save(); c.beginPath(); c.rect(x, y, w, h); c.clip();
        c.fillStyle = stripeColour;
        var prows = Math.ceil(h / pcell) + 1, pcols = Math.ceil(w / pcell) + 1;
        for (var pry = 0; pry < prows; pry++) {
          for (var pcx = 0; pcx < pcols; pcx++) {
            var ox = x + pcx * pcell + (pry % 2 ? pcell / 2 : 0) + pcell / 2;
            var oy = y + pry * pcell + pcell / 2;
            c.beginPath(); c.arc(ox, oy, prd, 0, Math.PI * 2); c.fill();
          }
        }
        c.restore();
      } else if (pat.type === 'chevron') {
        var cpx = w / (boardWmm || 210);
        var amp = Math.max(5, (pat.bandMm || 14) * cpx);
        c.save(); c.beginPath(); c.rect(x, y, w, h); c.clip();
        c.strokeStyle = stripeColour; c.lineWidth = Math.max(2, amp * 0.5); c.lineCap = 'butt';
        for (var cyy = y - h; cyy < y + h * 2; cyy += amp * 2) {
          c.beginPath();
          var up = true;
          for (var cxx = x; cxx <= x + w + amp; cxx += amp) {
            var cpy = cyy + (up ? 0 : amp);
            if (cxx === x) c.moveTo(cxx, cpy); else c.lineTo(cxx, cpy);
            up = !up;
          }
          c.stroke();
        }
        c.restore();
      } else if (pat.type === 'watercolour') {
        c.save(); c.beginPath(); c.rect(x, y, w, h); c.clip();
        paintWatercolourStripes(c, x, y, w, h, boardWmm, pat, stripeColour);
        c.restore();
      } else if (pat.type === 'accent') {
        // Keyline ring on the mount FACE, offset outward from the opening — not
        // a board fill, so it needs the aperture: `spec.apertureMm` {w,h},
        // centred on the board (how every customiser cuts it). Without it there
        // is nothing to offset from, so the board is left blank rather than
        // guessing a size — a wrong keyline is worse than none.
        var ap = spec.apertureMm;
        if (ap && ap.w && ap.h && boardWmm) {
          var apxmm = w / boardWmm;
          var aw = ap.w * apxmm, ah = ap.h * apxmm;
          var alw = Math.max(1, (pat.lineMm || 1.4) * apxmm);
          var alines = pat.lines || 1;
          var aspace = (pat.spacingMm || 0) * apxmm;
          // Rules grow outward, so on a narrow border the outer ones fail the
          // inside-the-board test first: it degrades to fewer rules, then none.
          var rings = [];
          for (var ai = 0; ai < alines; ai++) {
            var agap = Math.max(1, (pat.gapMm || 10) * apxmm + ai * aspace);
            var arx = x + (w - aw) / 2 - agap, ary = y + (h - ah) / 2 - agap;
            if (!(arx > x && ary > y)) break;
            rings.push({ x: arx, y: ary, w: aw + agap * 2, h: ah + agap * 2 });
          }
          if (rings.length) {
            c.save();
            if (pat.washAlpha && rings.length >= 2) {
              var r0 = rings[0], r1 = rings[1];
              c.globalAlpha = pat.washAlpha; c.fillStyle = stripeColour;
              c.beginPath();
              c.rect(r1.x, r1.y, r1.w, r1.h);
              c.rect(r0.x, r0.y, r0.w, r0.h);
              c.fill('evenodd');
              c.globalAlpha = 1;
            }
            c.strokeStyle = stripeColour; c.lineWidth = alw;
            rings.forEach(function (r) { c.strokeRect(r.x, r.y, r.w, r.h); });
            c.restore();
          }
        }
      }
      return;
    }
    // Plain board: in print mode leave it transparent (the real board is white);
    // in preview, fill the chosen base colour.
    if (printMode) return;
    c.fillStyle = (spec && spec.plainHex) || '#fafaf6';
    c.fillRect(x, y, w, h);
  }

  /* ----------------------------- app helpers ----------------------------- */

  function mmToPx(mm, dpi) { return Math.round((mm || 0) / 25.4 * (dpi || 300)); }

  // "857×610mm" / "857x610 mm" / "857 × 610" -> { w:857, h:610 }
  function parseFrameSizeMm(str) {
    if (!str) return null;
    var m = String(str).replace(/mm/ig, '').match(/(\d+(?:\.\d+)?)\s*[×x*]\s*(\d+(?:\.\d+)?)/i);
    return m ? { w: parseFloat(m[1]), h: parseFloat(m[2]) } : null;
  }

  // Map an order line's `_addons_custom` attrs -> paintMountFill spec.
  // Keys produced by pfc-moulding.js: Mount='Premium', Pattern=<label>,
  // 'Pattern colour'="Navy (#23395d)", 'Mount colour'=<Capitalised> (plain).
  function specFromOrderAttrs(attrs) {
    attrs = attrs || {};
    var isPremium = String(attrs.Mount || '').toLowerCase() === 'premium';
    var pattern = isPremium ? patternByLabel(attrs.Pattern) : null;
    var hexMatch = String(attrs['Pattern colour'] || '').match(/#([0-9a-f]{6}|[0-9a-f]{3})/i);
    var plain = String(attrs['Mount colour'] || '').toLowerCase() === 'black' ? '#1a1a1a' : '#fafaf6';
    return {
      premium: !!pattern,                                   // a real drawn pattern present
      pattern: pattern,
      stripeColour: hexMatch ? normaliseHex(hexMatch[0]) : '#888888',
      plainHex: plain
    };
  }

  // One-call print render. Returns the ctx's canvas. `ctx` is a 2D context whose
  // canvas is already sized to mmToPx(widthMm)/mmToPx(heightMm) at `dpi`.
  // Fills the WHOLE board (outer frame size); the cutter cuts the aperture.
  function renderMountPrint(ctx, opts) {
    var dpi = opts.dpi || 300;
    var w = mmToPx(opts.widthMm, dpi), h = mmToPx(opts.heightMm, dpi);
    paintMountFill(ctx, 0, 0, w, h, opts.widthMm, opts.spec);
    return ctx.canvas;
  }

  /* ---------- Printed personalisation (title strip) — VECTOR re-render ----------
     Ports drawTextBlock + the band geometry from pfc-moulding.js so the print-file
     admin app can re-draw the text crisply at any dpi from the `_pers_render` spec
     (mm-based band), instead of upscaling the stored 60dpi PNG. KEEP IN LOCKSTEP
     with drawTextBlock/persTextRect/drawPersonalisation in pfc-moulding.js. */
  function persFontCss(size, font) { return '600 ' + size + 'px "' + (font || 'serif') + '", serif'; }

  function drawPersTextBlock(ctx, lines, x, y, w, h, align, ink, capPx, font, exact) {
    lines = (lines || []).filter(function (t) { return t && t.trim(); });
    if (!lines.length) return;
    var n = lines.length, lineH = h / (n + 0.4), base = lineH * 0.74;
    if (capPx) base = Math.min(base, capPx);
    var size = base;
    lines.forEach(function (t) {
      if (exact) return;   // fit-by-space orders: capMm IS the printed size
      var s = base; ctx.font = persFontCss(s, font);
      while (ctx.measureText(t).width > w && s > 6) { s -= 1; ctx.font = persFontCss(s, font); }
      if (s < size) size = s;
    });
    ctx.font = persFontCss(size, font);
    ctx.fillStyle = ink; ctx.textBaseline = 'middle'; ctx.textAlign = align;
    var tx = align === 'left' ? x : align === 'right' ? (x + w) : (x + w / 2);
    var lh = size * 1.32;
    var startY = y + (h - lh * n) / 2 + lh / 2;
    lines.forEach(function (t, i) { ctx.fillText(t, tx, startY + i * lh); });
  }

  // Bounding box of the text block (for the patterned backing box).
  function persTextRect(ctx, lines, x, y, w, h, align, capPx, font, exact) {
    var ls = (lines || []).filter(function (t) { return t && t.trim(); });
    if (!ls.length) return null;
    var n = ls.length, lineH = h / (n + 0.4), base = lineH * 0.74, size = base;
    if (capPx) base = size = Math.min(base, capPx);
    ls.forEach(function (t) { if (exact) return; var s = base; ctx.font = persFontCss(s, font); while (ctx.measureText(t).width > w && s > 6) { s -= 1; ctx.font = persFontCss(s, font); } if (s < size) size = s; });
    ctx.font = persFontCss(size, font);
    var tw = 0; ls.forEach(function (t) { tw = Math.max(tw, ctx.measureText(t).width); });
    var tl = align === 'left' ? x : align === 'right' ? (x + w - tw) : (x + (w - tw) / 2);
    var lh = size * 1.32, startY = y + (h - lh * n) / 2 + lh / 2;
    return { x: tl, y: startY - size / 2, w: tw, h: (n - 1) * lh + size };
  }

  // Logo placement inside a zone: contain-fit, aligned like the text.
  function persLogoRect(img, x, y, w, h, align) {
    var iw = img.naturalWidth || img.width, ih = img.naturalHeight || img.height;
    if (!iw || !ih) return null;
    var s = Math.min(w / iw, h / ih), dw = iw * s, dh = ih * s;
    var dx = align === 'left' ? x : align === 'right' ? (x + w - dw) : (x + (w - dw) / 2);
    return { x: dx, y: y + (h - dh) / 2, w: dw, h: dh };
  }
  function persUnion(a, b) {
    if (!a) return b; if (!b) return a;
    var x1 = Math.min(a.x, b.x), y1 = Math.min(a.y, b.y);
    var x2 = Math.max(a.x + a.w, b.x + b.w), y2 = Math.max(a.y + a.h, b.y + b.h);
    return { x: x1, y: y1, w: x2 - x1, h: y2 - y1 };
  }

  // Draw the personalisation (text and/or logo) onto a board-sized ctx from a
  // `_pers_render` spec (see pfc-moulding.js renderPersExport). The board fill
  // must already be painted. Fonts must be registered by the host (node-canvas
  // registerFont). `logoImg` is the loaded `pr.logo.url` image, when the design
  // has a logo; the zone split mirrors the customisers' drawPersonalisation:
  // text + logo -> logo 42% / gap 6% / text the rest (logo above or below);
  // logo only -> a centred 58% slot.

  /* ---------- Styled lettering: occasion mount templates (_pers_render v2) ----------
     docs/SPEC-occasion-mount-templates.md. A v2 spec carries `style` (the
     RESOLVED lettering values, copied in at add-to-cart so a later edit to
     PERS_LETTERING never changes how an old order prints), `template`, and the
     two lines [names, date]. Drawn glyph by glyph so letter-spacing and
     synthesised small caps come out identical in the browser and node-canvas
     (neither exposes font-feature small caps, and ctx.letterSpacing is not in
     node-canvas). Text is never fitted down unless it would overflow the band. */
  var PERS_LETTERING = [
    { key: 'playfair',      label: 'High-contrast serif', names: { font: 'Playfair Display', weight: 500, caps: 'small', trackEm: 0.1 },  date: { font: 'Playfair Display', weight: 400, caps: 'small', trackEm: 0.1 } },
    { key: 'pinyon',        label: 'Copperplate script',  names: { font: 'Pinyon Script',    weight: 400, caps: 'as-typed', trackEm: 0, scale: 1.45, minMm: 7 }, date: { font: 'Cinzel', weight: 400, caps: 'small', trackEm: 0.1 } },
    { key: 'eb-garamond',   label: 'Old-style serif',     names: { font: 'EB Garamond',      weight: 500, caps: 'small', trackEm: 0.1 },  date: { font: 'EB Garamond', weight: 400, caps: 'small', trackEm: 0.1 } },
    { key: 'sacramento',    label: 'Modern handwritten',  names: { font: 'Sacramento',       weight: 400, caps: 'as-typed', trackEm: 0, scale: 1.6, minMm: 7 },  date: { font: 'Jost', weight: 400, caps: 'all', trackEm: 0.22 } },
    { key: 'jost',          label: 'Geometric sans',      names: { font: 'Jost',             weight: 500, caps: 'all', trackEm: 0.24 },   date: { font: 'Jost', weight: 400, caps: 'all', trackEm: 0.24 } },
    { key: 'poiret',        label: 'Art deco',            names: { font: 'Poiret One',       weight: 400, caps: 'as-typed', trackEm: 0.06, scale: 1.3, minMm: 7 }, date: { font: 'Poiret One', weight: 400, caps: 'all', trackEm: 0.2 } },
    { key: 'special-elite', label: 'Typewriter',          names: { font: 'Special Elite',    weight: 400, caps: 'as-typed', trackEm: 0.02 }, date: { font: 'Special Elite', weight: 400, caps: 'as-typed', trackEm: 0.02 } }
  ];
  // Ink palette. `dark: true` = only offered on dark boards. Black is the
  // default and resolves to white on a dark board, as the auto ink always has.
  var PERS_INKS = [
    { key: 'black',     label: 'Black',      hex: '#1a1a1a' },
    { key: 'charcoal',  label: 'Charcoal',   hex: '#3a3835' },
    { key: 'warm-grey', label: 'Warm grey',  hex: '#7b7268' },
    { key: 'navy',      label: 'Navy',       hex: '#23395d' },
    { key: 'ruby',      label: 'Ruby',       hex: '#8e1b2c' },   // deep red; ruby (40th) anniversaries
    { key: 'gold-tone', label: 'Gold-tone',  hex: '#a8843f' },   // printed ochre, NOT foil
    { key: 'white',     label: 'White',      hex: '#ffffff', dark: true }
  ];
  var PERS_TEMPLATES = [
    { key: 'plain',    label: 'Plain',       layout: 'lines',    keyline: null },
    { key: 'monogram', label: 'Monogram',    layout: 'monogram', keyline: null },
    { key: 'french',   label: 'French line', layout: 'lines',    keyline: 'accent-double' }
  ];
  function letteringByKey(k) { return PERS_LETTERING.find(function (l) { return l.key === k; }) || PERS_LETTERING[0]; }
  function persTemplateByKey(k) { return PERS_TEMPLATES.find(function (t) { return t.key === k; }) || PERS_TEMPLATES[0]; }
  function isDarkHex(hex) {
    var h = normaliseHex(hex); if (!h) return false;
    h = h.slice(1); if (h.length === 3) h = h.split('').map(function (c) { return c + c; }).join('');
    var r = parseInt(h.substr(0, 2), 16), g = parseInt(h.substr(2, 2), 16), b = parseInt(h.substr(4, 2), 16);
    return ((0.299 * r + 0.587 * g + 0.114 * b) / 255) <= 0.55;
  }
  // Ink hex for a palette key on a board colour. On a dark board the dark inks
  // would vanish, so they print white; white on a light board prints black.
  function persInkHex(key, boardHex) {
    var ink = PERS_INKS.find(function (i) { return i.key === key; }) || PERS_INKS[0];
    var dark = isDarkHex(boardHex || STRIPE_BASE_HEX);
    if (dark && (ink.key === 'black' || ink.key === 'charcoal' || ink.key === 'navy' || ink.key === 'ruby')) return '#ffffff';
    if (!dark && ink.dark) return PERS_INKS[0].hex;
    return ink.hex;
  }

  function styledFont(spec, sizePx) { return (spec.italic ? 'italic ' : '') + (spec.weight || 400) + ' ' + sizePx + 'px "' + spec.font + '", serif'; }
  // One run of glyphs: [{c, size}] with synthesised small caps (lowercase ->
  // uppercase at 0.78).
  function styledRun(text, caps, sizePx) {
    var out = [];
    Array.from(String(text || '')).forEach(function (ch) {
      var up = ch.toUpperCase(), size = sizePx, c = ch;
      if (caps === 'all') c = up;
      else if (caps === 'small' && ch !== up) { c = up; size = sizePx * 0.78; }
      out.push({ c: c, size: size });
    });
    return out;
  }
  function styledRunWidth(ctx, run, spec, trackPx) {
    var w = 0;
    run.forEach(function (g, i) { ctx.font = styledFont(spec, g.size); w += ctx.measureText(g.c).width + (i < run.length - 1 ? trackPx : 0); });
    return w;
  }
  function drawStyledRun(ctx, run, spec, trackPx, cx, baseline, ink) {
    var x = cx - styledRunWidth(ctx, run, spec, trackPx) / 2;
    ctx.fillStyle = ink; ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
    run.forEach(function (g) { ctx.font = styledFont(spec, g.size); ctx.fillText(g.c, x, baseline); x += ctx.measureText(g.c).width + trackPx; });
  }
  function monogramInitials(names) {
    var parts = String(names || '').split(/\s*(?:&|\band\b|\+)\s*/i).map(function (p) { return p.trim(); }).filter(Boolean);
    if (parts.length >= 2) return [parts[0][0].toUpperCase(), parts[1][0].toUpperCase()];
    return [((parts[0] || ' ')[0] || ' ').toUpperCase(), ''];
  }

  // Lay out a v2 spec inside a box (px). Returns the draw list WITHOUT drawing,
  // so callers can measure (fit checks, backing boxes) with the same maths.
  // capPx = the names size before the lettering's own scale.
  function layoutStyled(ctx, pr, box, capPx) {
    var st = pr.style, nm = st.names, dt = st.date;
    var names = (pr.lines && pr.lines[0]) || '', date = (pr.lines && pr.lines[1]) || '';
    var hasDate = !!date.trim();
    var monogram = pr.template === 'monogram';
    var nameSize = capPx * (monogram ? 2 : (nm.scale || 1));
    var dateSize = monogram ? Math.max(capPx * 0.62, (pr.minDateMm || 0) * (capPx / pr.capMm)) : capPx * 0.72;
    var items = [], fit = 1;
    function lay(k) {
      var ns = nameSize * k, ds = dateSize * k, ws = [];
      var nRun, nTrack, nW;
      if (monogram) {
        var ini = monogramInitials(names), mono = ini[1] ? ini[0] + ' & ' + ini[1] : ini[0];
        nRun = styledRun(mono, 'as-typed', ns); nTrack = 0;
      } else { nRun = styledRun(names, nm.caps, ns); nTrack = (nm.trackEm || 0) * ns; }
      nW = styledRunWidth(ctx, nRun, nm, nTrack); ws.push(nW);
      var dRun = hasDate ? styledRun(date, dt.caps, ds) : null, dTrack = (dt.trackEm || 0) * ds;
      if (dRun) ws.push(styledRunWidth(ctx, dRun, dt, dTrack));
      // Monogram: leave room for script descenders (J, y) above the rule.
      var lh1 = ns * 0.72, lh2 = ds * 0.72, gap = monogram ? ns * 0.3 + ds * 1.4 : capPx * k * 0.6;
      var blockH = lh1 + (hasDate ? gap + lh2 : 0);
      return { ns: ns, ds: ds, nRun: nRun, nTrack: nTrack, dRun: dRun, dTrack: dTrack, w: Math.max.apply(null, ws), lh1: lh1, lh2: lh2, gap: gap, blockH: blockH };
    }
    var L = lay(1);
    if (L.w > box.w || L.blockH > box.h) { fit = Math.min(box.w / L.w, box.h / L.blockH) * 0.98; L = lay(fit); }
    var cx = box.x + box.w / 2, base1 = box.y + (box.h - L.blockH) / 2 + L.lh1;
    items.push({ run: L.nRun, spec: monogram ? { font: nm.font, weight: nm.weight } : nm, track: L.nTrack, cx: cx, base: base1 });
    var rule = null;
    if (L.dRun) {
      var base2 = base1 + L.gap + L.lh2;
      items.push({ run: L.dRun, spec: dt, track: L.dTrack, cx: cx, base: base2 });
      // The rule sits just above the date, below any descender.
      if (monogram) { var ppm = capPx / pr.capMm; rule = { x1: cx - 9 * ppm, x2: cx + 9 * ppm, y: base2 - L.lh2 - L.ds * 0.7, w: Math.max(0.5, 0.25 * ppm) }; }
    }
    return { items: items, rule: rule, fit: fit, rect: { x: cx - L.w / 2, y: base1 - L.lh1, w: L.w, h: L.blockH } };
  }
  function renderPersStyled(ctx, pr, dpi, printMode) {
    var ppm = (dpi || 300) / 25.4;
    var b = { x: pr.bandMm.x * ppm, y: pr.bandMm.y * ppm, w: pr.bandMm.w * ppm, h: pr.bandMm.h * ppm };
    var pad = (pr.boxPadMm != null ? pr.boxPadMm : 3) * ppm;
    var inner = { x: b.x + pad, y: b.y + pad, w: b.w - pad * 2, h: b.h - pad * 2 };
    if (inner.w <= 4 || inner.h <= 4) inner = b;
    return drawStyledBox(ctx, pr, inner, (pr.capMm || 5) * ppm);
  }
  // Draw a v2 spec into a px box (already inside the box padding). The live
  // customiser preview calls this directly, so preview and print share it.
  function drawStyledBox(ctx, pr, box, capPx) {
    var ink = pr.inkHex || '#1a1a1a';
    var L = layoutStyled(ctx, pr, box, capPx);
    ctx.save();
    L.items.forEach(function (it) { drawStyledRun(ctx, it.run, it.spec, it.track, it.cx, it.base, ink); });
    if (L.rule) { ctx.strokeStyle = ink; ctx.lineWidth = L.rule.w; ctx.beginPath(); ctx.moveTo(L.rule.x1, L.rule.y); ctx.lineTo(L.rule.x2, L.rule.y); ctx.stroke(); }
    ctx.restore();
    return L;
  }
  // Fonts (family + weight) a v2 spec needs registered before it can print.
  function styledFonts(pr) {
    if (!pr || !pr.style) return [];
    var out = [], seen = {};
    [pr.style.names, pr.style.date].forEach(function (s) {
      if (!s) return; var k = s.font + '@' + (s.weight || 400);
      if (!seen[k]) { seen[k] = 1; out.push({ family: s.font, weight: String(s.weight || 400) }); }
    });
    return out;
  }

  function renderPersonalisationText(ctx, pr, dpi, printMode, logoImg) {
    if (!pr || !pr.bandMm) return;
    if (pr.v >= 2 && pr.style) { renderPersStyled(ctx, pr, dpi, printMode); return; }
    var ppm = (dpi || 300) / 25.4;
    var b = { x: pr.bandMm.x * ppm, y: pr.bandMm.y * ppm, w: pr.bandMm.w * ppm, h: pr.bandMm.h * ppm };
    var pad = (pr.boxPadMm != null ? pr.boxPadMm : 3) * ppm;
    var inner = { x: b.x + pad, y: b.y + pad, w: b.w - pad * 2, h: b.h - pad * 2 };
    if (inner.w <= 4 || inner.h <= 4) inner = b;
    var align = pr.align || 'center';
    var capPx = (pr.capMm || 7) * ppm;
    var exact = !!pr.exact;
    var hasText = (pr.lines || []).some(function (t) { return t && t.trim(); });
    var logo = (pr.logo && logoImg) ? logoImg : null;
    if (!hasText && !logo) return;

    var textZone = null, logoZone = null;
    if (hasText && logo) {
      var gap = inner.h * 0.06, logoH = inner.h * 0.42, textH = inner.h - logoH - gap;
      if (pr.logo.pos === 'below') {
        textZone = { x: inner.x, y: inner.y, w: inner.w, h: textH };
        logoZone = { x: inner.x, y: inner.y + textH + gap, w: inner.w, h: logoH };
      } else {
        logoZone = { x: inner.x, y: inner.y, w: inner.w, h: logoH };
        textZone = { x: inner.x, y: inner.y + logoH + gap, w: inner.w, h: textH };
      }
    } else if (hasText) {
      textZone = inner;
    } else {
      var lh = inner.h * 0.58;
      logoZone = { x: inner.x, y: inner.y + (inner.h - lh) / 2, w: inner.w, h: lh };
    }

    // Patterned board: a backing box behind the text/logo for legibility. In
    // preview it's filled in the mount base colour; in print mode it KNOCKS OUT
    // the pattern (→ transparent → the real white board shows through), so no
    // off-white ink is laid down.
    if (pr.patterned) {
      var tr = null;
      if (logoZone) tr = persUnion(tr, persLogoRect(logo, logoZone.x, logoZone.y, logoZone.w, logoZone.h, align));
      if (textZone) tr = persUnion(tr, persTextRect(ctx, pr.lines, textZone.x, textZone.y, textZone.w, textZone.h, align, capPx, pr.font, exact));
      if (tr && tr.w > 0 && tr.h > 0) {
        var bx = Math.max(b.x, tr.x - pad), by = Math.max(b.y, tr.y - pad);
        var bw = Math.min(b.x + b.w, tr.x + tr.w + pad) - bx;
        var bh = Math.min(b.y + b.h, tr.y + tr.h + pad) - by;
        var br = Math.min(bw, bh) * 0.2;
        ctx.save(); ctx.beginPath();
        if (ctx.roundRect) ctx.roundRect(bx, by, bw, bh, br); else ctx.rect(bx, by, bw, bh);
        if (printMode) { ctx.globalCompositeOperation = 'destination-out'; ctx.fillStyle = '#000'; }
        else { ctx.fillStyle = pr.mountBaseHex || '#fafaf6'; }
        ctx.fill(); ctx.restore();
      }
    }
    if (logoZone) {
      var lr = persLogoRect(logo, logoZone.x, logoZone.y, logoZone.w, logoZone.h, align);
      if (lr) ctx.drawImage(logo, lr.x, lr.y, lr.w, lr.h);
    }
    if (textZone) drawPersTextBlock(ctx, pr.lines, textZone.x, textZone.y, textZone.w, textZone.h, align, pr.inkHex || '#1a1a1a', capPx, pr.font, exact);
  }

  return {
    STRIPE_BASE_HEX: STRIPE_BASE_HEX,
    MOUNT_PATTERNS: MOUNT_PATTERNS,
    STRIPE_PRESETS: STRIPE_PRESETS,
    normaliseHex: normaliseHex,
    patternByKey: patternByKey,
    patternByLabel: patternByLabel,
    patternGroups: patternGroups,
    groupOf: groupOf,
    stripeBandCount: stripeBandCount,
    paintMountFill: paintMountFill,
    mmToPx: mmToPx,
    parseFrameSizeMm: parseFrameSizeMm,
    specFromOrderAttrs: specFromOrderAttrs,
    renderMountPrint: renderMountPrint,
    renderPersonalisationText: renderPersonalisationText,
    PERS_LETTERING: PERS_LETTERING,
    PERS_INKS: PERS_INKS,
    PERS_TEMPLATES: PERS_TEMPLATES,
    letteringByKey: letteringByKey,
    persTemplateByKey: persTemplateByKey,
    persInkHex: persInkHex,
    layoutStyled: layoutStyled,
    renderPersStyled: renderPersStyled,
    drawStyledBox: drawStyledBox,
    styledFonts: styledFonts
  };
});
